import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getLeaveRequests, saveLeaveRequest, processLeaveRequest, checkLeaveConflicts, GetLeaveRequestsOutputType, CheckLeaveConflictsOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { CalendarDays, Plus, Check, X as XIcon, Download } from 'lucide-react';
import { isManagerPlus } from '@/lib/roles';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type Req = GetLeaveRequestsOutputType['requests'][0];
const STATUS_COLORS: Record<string, string> = { Draft: 'secondary', Submitted: 'default', Approved: 'default', Rejected: 'destructive', Cancelled: 'secondary' };
const REQUEST_TYPES = ['Annual Leave', 'Sick Leave', 'Unpaid Leave', 'Compassionate Leave', 'Availability Change', 'Unavailability Note', 'Other'];

function fmtDate(d?: string) { if (!d) return '—'; try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; } }

export default function LeaveRequests() {
  const { user } = useAuth();
  const isManager = isManagerPlus(user?.role);
  const [tab, setTab] = useState('own');
  const [requests, setRequests] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ requestType: '', startDate: '', endDate: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewReason, setReviewReason] = useState('');
  const [internalNote, setInternalNote] = useState('');

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getLeaveRequests({ scope: tab === 'team' && isManager ? 'team' : 'own', status: statusFilter !== 'all' ? statusFilter : undefined })
      .then(d => setRequests(d.requests))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, tab, statusFilter, isManager]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.requestType || !form.startDate || !form.endDate) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      await saveLeaveRequest({ requestType: form.requestType, startDate: form.startDate, endDate: form.endDate, reason: form.reason, status: 'Submitted' });
      toast.success('Request submitted');
      setShowNew(false);
      setForm({ requestType: '', startDate: '', endDate: '', reason: '' });
      load();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleAction = async (id: string, action: 'approve' | 'reject' | 'cancel') => {
    try {
      await processLeaveRequest({ id, action, reviewReason, internalNote });
      toast.success(`Request ${action}d`);
      setReviewId(null);
      setReviewReason('');
      setInternalNote('');
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const exportCsv = () => {
    const headers = ['Staff', 'Type', 'Start', 'End', 'Status', 'Reason'];
    const rows = requests.map(r => [r.requesterName, r.requestType, r.startDate || '', r.endDate || '', r.status, r.reason || '']);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'leave-requests.csv'; a.click();
  };

  const [conflicts, setConflicts] = useState<CheckLeaveConflictsOutputType['conflicts']>([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);

  useEffect(() => {
    if (reviewId && isManager) {
      setLoadingConflicts(true);
      setConflicts([]);
      checkLeaveConflicts({ leaveRequestId: reviewId })
        .then(r => setConflicts(r.conflicts))
        .catch(() => {})
        .finally(() => setLoadingConflicts(false));
    }
  }, [reviewId, isManager]);

  const reviewReq = requests.find(r => r.id === reviewId);

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Leave Requests</h1>
            <p className="text-sm text-muted-foreground">Submit and track leave requests</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isManager && <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" />CSV</Button>}
          <Button size="sm" onClick={() => setShowNew(true)}><Plus className="w-4 h-4 mr-1.5" />New Request</Button>
        </div>
      </div>

      {isManager && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList><TabsTrigger value="own">My Requests</TabsTrigger><TabsTrigger value="team">Team Queue</TabsTrigger></TabsList>
        </Tabs>
      )}

      <div className="flex gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Submitted">Submitted</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No requests found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <Card key={r.id}>
              <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{r.requestType}</p>
                    <Badge variant={STATUS_COLORS[r.status] as any || 'secondary'}>{r.status}</Badge>
                    {(r as any).xeroSyncStatus && (r as any).xeroSyncStatus !== 'Pending' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${(r as any).xeroSyncStatus === 'Synced' ? 'bg-primary/10 text-primary' : (r as any).xeroSyncStatus === 'Sync Failed' ? 'bg-destructive/10 text-destructive' : 'bg-accent text-accent-foreground'}`}>
                        {(r as any).xeroSyncStatus}
                      </span>
                    )}
                    {(r as any).xeroSyncStatus && (r as any).xeroSyncStatus !== 'Pending' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${(r as any).xeroSyncStatus === 'Synced' ? 'bg-primary/10 text-primary' : (r as any).xeroSyncStatus === 'Sync Failed' ? 'bg-destructive/10 text-destructive' : 'bg-accent text-accent-foreground'}`}>
                        {(r as any).xeroSyncStatus}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(r.startDate)} — {fmtDate(r.endDate)}</p>
                  {tab === 'team' && <p className="text-xs text-muted-foreground">{r.requesterName}</p>}
                  {r.reason && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{r.reason}</p>}
                  {r.reviewReason && <p className="text-xs mt-1"><span className="font-medium">Review:</span> {r.reviewReason}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status === 'Submitted' && tab === 'own' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="outline" size="sm">Cancel</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Cancel request?</AlertDialogTitle><AlertDialogDescription>This will cancel your leave request.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction onClick={() => handleAction(r.id, 'cancel')}>Cancel Request</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {r.status === 'Submitted' && tab === 'team' && isManager && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="text-green-600" onClick={() => { setReviewId(r.id); }}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setReviewId(r.id); }}><XIcon className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Request Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Leave / Availability Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Type *</Label>
              <Select value={form.requestType} onValueChange={v => setForm(f => ({ ...f, requestType: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date *</Label><Input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} /></div>
              <div><Label>End Date *</Label><Input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} /></div>
            </div>
            <div><Label>Reason / Notes</Label><Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} /></div>
          </div>
          <DialogFooter><Button onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Request'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={!!reviewId} onOpenChange={o => { if (!o) setReviewId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Review: {reviewReq?.requestType}</DialogTitle></DialogHeader>
          {reviewReq && (
            <div className="space-y-3">
              <p className="text-sm"><strong>Staff:</strong> {reviewReq.requesterName}</p>
              <p className="text-sm"><strong>Dates:</strong> {fmtDate(reviewReq.startDate)} — {fmtDate(reviewReq.endDate)}</p>
              {reviewReq.reason && <p className="text-sm"><strong>Reason:</strong> {reviewReq.reason}</p>}
              {loadingConflicts && <p className="text-xs text-muted-foreground animate-pulse">Checking for conflicts...</p>}
              {conflicts.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 space-y-1">
                  <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">⚠️ Conflict Warnings</p>
                  {conflicts.map((c, i) => (
                    <p key={i} className="text-xs text-amber-300">{c.message}</p>
                  ))}
                  <p className="text-[10px] text-muted-foreground">Warnings do not block approval — manager may override.</p>
                </div>
              )}
              <div><Label>Review Reason</Label><Textarea value={reviewReason} onChange={e => setReviewReason(e.target.value)} rows={2} /></div>
              <div><Label>Internal Note (managers only)</Label><Textarea value={internalNote} onChange={e => setInternalNote(e.target.value)} rows={2} /></div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="text-red-600" onClick={() => handleAction(reviewReq.id, 'reject')}>Reject</Button>
                <Button onClick={() => handleAction(reviewReq.id, 'approve')}>Approve</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
